import React, { createContext, useCallback, useContext, useState } from 'react'
import { AgoraEngine } from '../rtc/agoraEngine'
import { VirtualBackgroundMode } from '../types'

interface VirtualBackgroundContextValue {
  mode: VirtualBackgroundMode
  imageUrl: string | null
  setNone: () => Promise<void>
  setBlur: () => Promise<void>
  setImage: (url: string) => Promise<void>
}

const VirtualBackgroundContext =
  createContext<VirtualBackgroundContextValue | null>(null)

interface Props {
  engine: AgoraEngine
  children: React.ReactNode
}

export function VirtualBackgroundProvider({ engine, children }: Props) {
  const [mode, setMode] = useState<VirtualBackgroundMode>('none')
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const setNone = useCallback(async () => {
    await engine.setVirtualBackgroundNone()
    setMode('none')
    setImageUrl(null)
  }, [engine])

  const setBlur = useCallback(async () => {
    await engine.setVirtualBackgroundBlur()
    setMode('blur')
    setImageUrl(null)
  }, [engine])

  const setImage = useCallback(
    async (url: string) => {
      await engine.setVirtualBackgroundImage(url)
      setMode('image')
      setImageUrl(url)
    },
    [engine]
  )

  return (
    <VirtualBackgroundContext.Provider
      value={{ mode, imageUrl, setNone, setBlur, setImage }}
    >
      {children}
    </VirtualBackgroundContext.Provider>
  )
}

export function useVirtualBackground() {
  const context = useContext(VirtualBackgroundContext)
  if (!context) {
    throw new Error(
      'useVirtualBackground precisa ser usado com VirtualBackgroundProvider'
    )
  }
  return context
}
